"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { IconComponent } from "@repo/ui/components/icons";
import type {
  SectionProps,
  StatsSectionData,
} from "@/themes/default/templates/section/types";
import { getLocalizedText } from "@/themes/default/templates/section/utils";

/**
 * epona Stats — Epona "Facts & Figures" counter row.
 *
 * The counters sit on the signature deep-blue Epona gradient band with
 * big white Oswald numerals (`ci926-stat-number`) that count up when the
 * section scrolls into view. Optional per-counter icons render above the
 * number; a small uppercase label sits below.
 *
 * The `stats` schema's per-counter colour overrides are intentionally
 * ignored — the gradient band owns the palette so the row reads as one
 * cohesive Epona block.
 */

interface ParsedCount {
  prefix: string;
  suffix: string;
  number: number | null;
  hasComma: boolean;
  decimals: number;
}

/** Split "4,500+" → { prefix:"", number:4500, suffix:"+", hasComma:true }. */
function parseCount(raw: string): ParsedCount {
  const str = String(raw ?? "");
  const match = str.match(/[\d.,]+/);
  if (!match) return { prefix: str, suffix: "", number: null, hasComma: false, decimals: 0 };
  const numStr = match[0];
  const idx = match.index ?? 0;
  const number = parseFloat(numStr.replace(/,/g, ""));
  if (Number.isNaN(number))
    return { prefix: str, suffix: "", number: null, hasComma: false, decimals: 0 };
  return {
    prefix: str.slice(0, idx),
    suffix: str.slice(idx + numStr.length),
    number,
    hasComma: numStr.includes(","),
    decimals: (numStr.split(".")[1] || "").length,
  };
}

function formatNumber(value: number, decimals: number, hasComma: boolean): string {
  const fixed = value.toFixed(decimals);
  if (!hasComma) return fixed;
  const [intPart, decPart] = fixed.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart ? `${grouped}.${decPart}` : grouped;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

/** Single animated counter — counts up once `start` flips true. */
function Counter({ raw, start }: { raw: string; start: boolean }) {
  const parsed = useMemo(() => parseCount(raw), [raw]);
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start || parsed.number === null) return;
    const target = parsed.number;
    const duration = 1500;
    const t0 = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const p = Math.min((now - t0) / duration, 1);
      setValue(target * easeOutCubic(p));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [start, parsed.number]);

  if (parsed.number === null) return <>{raw}</>;
  return (
    <>
      {parsed.prefix}
      {formatNumber(value, parsed.decimals, parsed.hasComma)}
      {parsed.suffix}
    </>
  );
}

export function StatsSection({
  section,
  currentLanguage = "en",
}: SectionProps<StatsSectionData>) {
  const headline = getLocalizedText(section.headline, currentLanguage);
  const description = getLocalizedText(section.description, currentLanguage);
  const counters = section.counters ?? [];
  const colsRaw = section.columns ?? 3;
  const cols = Number(colsRaw) || 3;

  const sectionRef = useRef<HTMLElement | null>(null);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || started) return;
    if (typeof IntersectionObserver === "undefined") {
      setStarted(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);

  if (counters.length === 0) return null;

  // Editorial variant — minimal Crystal-Image facts strip: big light
  // brand-gradient numerals + small labels, divided by hairlines, on the
  // page background (no gradient band).
  if ((section as any).variant === "editorial") {
    return (
      <section
        className="w-full border-y border-border"
        style={{ background: "color-mix(in srgb, var(--color-muted, #f1f5f9) 45%, transparent)" }}
        data-section-id={section._id}
        data-section-type="stats"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-2 px-5 sm:px-6 md:grid-cols-4 lg:px-8">
          {counters.map((c, idx) => (
            <div
              key={idx}
              className={`py-8 md:py-10 ${idx % 2 === 1 ? "pl-6 border-l border-border" : "pr-6"} md:px-8 ${idx > 0 ? "md:border-l md:border-border" : ""}`}
            >
              <div
                className="text-3xl leading-none sm:text-4xl md:text-[2.7rem]"
                style={{
                  fontFamily: '"Helvetica Neue", "Segoe UI", Arial, sans-serif',
                  fontWeight: 300,
                  letterSpacing: "-0.02em",
                  fontVariantNumeric: "tabular-nums",
                  background: "linear-gradient(100deg,var(--ci926-accent-from) 0%,var(--ci926-accent-to) 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                {c.count}
              </div>
              <div className="mt-2 text-[13px] text-muted-foreground">
                {getLocalizedText(c.title, currentLanguage)}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Cards variant — Crystal-Image counter grid: subtle band + header +
  // count-up cards with big brand-gradient numbers (used for the
  // "University Statistics" block below the rector).
  if ((section as any).variant === "cards") {
    const gc = cols <= 2 ? "sm:grid-cols-2" : cols === 3 ? "sm:grid-cols-2 md:grid-cols-3" : "sm:grid-cols-2 md:grid-cols-4";
    return (
      <section
        ref={sectionRef as React.RefObject<HTMLElement>}
        className="w-full border-y border-border"
        style={{ background: "color-mix(in srgb, var(--color-muted, #f1f5f9) 45%, transparent)" }}
        data-section-id={section._id}
        data-section-type="stats"
      >
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8">
          <div className="mb-10 text-center md:mb-12">
            <p className="mb-3 text-xs font-medium uppercase" style={{ letterSpacing: "0.22em", color: "var(--color-primary,#1598CB)", fontFamily: "ui-monospace,Menlo,monospace" }}>
              {currentLanguage === "mm" ? "ကိန်းဂဏန်းများ" : "By the numbers"}
            </p>
            {headline && (
              <h2 className="text-3xl md:text-[2.4rem]" style={{ fontFamily: '"Helvetica Neue","Segoe UI",Arial,sans-serif', fontWeight: 300, letterSpacing: "-0.01em" }}>{headline}</h2>
            )}
          </div>
          <div className={`grid grid-cols-2 gap-4 ${gc}`}>
            {counters.map((c, idx) => (
              <div key={idx} className="rounded-2xl border border-border bg-[color:var(--color-card,transparent)] px-5 py-8 text-center transition-transform hover:-translate-y-1"
                style={{ boxShadow: "0 18px 44px -32px rgba(9,30,60,0.32)" }}>
                <div className="leading-none" style={{
                  fontFamily: '"Helvetica Neue","Segoe UI",Arial,sans-serif', fontWeight: 300,
                  fontSize: "clamp(2rem,4vw,3rem)", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums",
                  background: "linear-gradient(100deg,var(--ci926-accent-from) 0%,var(--ci926-accent-to) 100%)",
                  WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
                }}>
                  <Counter raw={c.count} start={started} />
                </div>
                <div className="mt-2.5 text-[13px] text-muted-foreground">{getLocalizedText(c.title, currentLanguage)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const gridCols =
    cols === 1
      ? "grid-cols-1"
      : cols === 2
        ? "grid-cols-1 md:grid-cols-2"
        : cols === 3
          ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
          : "grid-cols-2 md:grid-cols-4";

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      // Paints its own Epona gradient band (covers any zebra wrapper).
      className="w-full bg-ci926-gradient"
      data-section-id={section._id}
      data-section-type="stats"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        {(headline || description) && (
          <div className="mb-10 md:mb-14 max-w-3xl mx-auto text-center">
            <div
              className="ci926-section-eyebrow mb-3"
              style={{ color: "rgba(255,255,255,0.85)" }}
            >
              {currentLanguage === "mm" ? "ကိန်းဂဏန်းများ" : "Facts & Figures"}
            </div>
            {headline && (
              <h2 className="ci926-section-title ci926-section-title--underline text-white text-2xl md:text-3xl lg:text-4xl">
                {headline}
              </h2>
            )}
            {description && (
              <p
                className="mt-4 text-base md:text-lg"
                style={{
                  color: "rgba(255,255,255,0.9)",
                  fontFamily: "var(--font-sans)",
                }}
              >
                {description}
              </p>
            )}
          </div>
        )}

        <div className={`grid ${gridCols} gap-8 md:gap-12`}>
          {counters.map((c, idx) => (
            <div key={idx} className="text-center">
              {c.icon && (
                <div
                  className="mb-3 flex justify-center"
                  style={{ color: "#FFFFFF" }}
                  aria-hidden="true"
                >
                  <IconComponent
                    name={c.icon}
                    size={40}
                    color={"#FFFFFF" as unknown as string}
                  />
                </div>
              )}
              <div className="ci926-stat-number text-white text-4xl md:text-5xl lg:text-6xl">
                <Counter raw={c.count} start={started} />
              </div>
              <div
                className="mt-2 text-sm uppercase tracking-wider"
                style={{
                  color: "rgba(255,255,255,0.85)",
                  fontFamily: "var(--font-sans)",
                }}
              >
                {getLocalizedText(c.title, currentLanguage)}
              </div>
              {c.description && (
                <div
                  className="mt-1 text-xs"
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    fontFamily: "var(--font-sans)",
                  }}
                >
                  {getLocalizedText(c.description, currentLanguage)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default StatsSection;
